import type { CSSProperties } from 'react';
import { useT } from '../i18n.tsx';

/**
 * Der Kartenvorrat als kleiner Stapel.
 *
 * Vorher stand dort nur „218 Karten". Eine Zahl ist abstrakt; ein
 * Stapel ist etwas, das kleiner wird. Über einen Spieleabend hinweg
 * sieht man den Vorrat schwinden, ohne die Zahl lesen zu müssen.
 */
/**
 * Wie viele Kanten der Stapel bei welchem Vorrat zeigt.
 *
 * Der Kommentar oben versprach „man sieht den Vorrat schwinden" -- die
 * Grafik hatte aber immer exakt drei Kanten, ob 218 Karten oder 5. Nur
 * die Zahl aenderte sich, und die zu lesen war ja gerade der Punkt, den
 * der Stapel ersparen sollte.
 *
 * Absichtlich grob gestuft: eine Kante pro Karte waere bei 200 Karten
 * Matsch, und die genaue Zahl steht ohnehin daneben. Es geht um den
 * Unterschied zwischen „reichlich" und „gleich leer".
 */
const STACK_STEPS = [
  { min: 120, layers: 5 },
  { min: 60, layers: 4 },
  { min: 20, layers: 3 },
  { min: 8, layers: 2 },
  { min: 0, layers: 1 },
] as const;

function layersFor(remaining: number): number {
  return STACK_STEPS.find((step) => remaining >= step.min)?.layers ?? 1;
}

export function Deck({ remaining }: { remaining: number }) {
  const t = useT();

  // Unter zwanzig Karten wird es knapp -- das darf auffallen.
  const low = remaining < 20;
  const layers = layersFor(remaining);

  return (
    <span
      className={`deck ${low ? 'deck--low' : ''}`}
      title={t('game.deckRemaining', { count: remaining })}
    >
      <span className="deck__stack" aria-hidden="true">
        {/*
         * `--layer` zaehlt von HINTEN: die zuletzt gerenderte Kante
         * liegt oben und bekommt 0, also keinen Versatz. Andersherum
         * saesse die oberste Karte schief auf dem Stapel.
         */}
        {Array.from({ length: layers }, (_, index) => (
          <span key={index} style={{ '--layer': layers - 1 - index } as CSSProperties} />
        ))}
      </span>
      <span className="deck__count">{remaining}</span>
    </span>
  );
}
